import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import type { Signal } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import type { OnyxChip } from '@onyx/angular';
import {
  OnyxChipsComponent,
  OnyxDropdownDirective,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxModalComponent,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { isString } from 'lodash';
import {
  catchError,
  EMPTY,
  filter,
  first,
  merge,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { ModalNoteComponent } from '../../../common/components/modals/modal-note/modal-note.component';
import { DictionaryCode } from '../../../common/enums/dictionary-code';
import { CommonHelper } from '../../../common/helpers/common.helper';
import { FleetIdentifierPipe } from '../../../common/pipes/fleet-identifier.pipe';
import { ModalsStore } from '../../../common/stores/modals.store';
import type { Order } from '../../orders/common/interfaces/order';
import { OrdersService } from '../../orders/common/services/orders.service';
import { FleetRoute } from '../common/enums/fleet-route';
import { FleetRouterStateKey } from '../common/enums/fleet-router-state-key';
import {
  FLEET_HEADER_ACTIONS,
  FleetHelper,
} from '../common/helpers/fleet.helper';
import { FleetTypeHelper } from '../common/helpers/fleet-type.helper';
import type { Fleet, SimplifiedFleet } from '../common/interfaces/fleet';
import { FleetService } from '../common/services/fleet.service';
import { FleetModalHeaderDescriptionComponent } from './fleet-modal-header-description/fleet-modal-header-description.component';
import { FleetModalDetailsComponent } from './fleet-modal-sections/fleet-modal-details/fleet-modal-details.component';
import { FleetModalDocumentsComponent } from './fleet-modal-sections/fleet-modal-documents/fleet-modal-documents.component';
import { FleetModalSummaryComponent } from './fleet-modal-sections/fleet-modal-summary/fleet-modal-summary.component';
import { FleetModalWarningsComponent } from './fleet-modal-warnings/fleet-modal-warnings.component';

export type FleetModalData = Fleet | SimplifiedFleet | string;

export enum FleetModalSection {
  SUMMARY,
  DETAILS,
  DOCUMENTS,
  NOTE,
}

@Component({
  selector: 'app-fleet-modal',
  imports: [
    OnyxModalComponent,
    FleetIdentifierPipe,
    OnyxIconComponent,
    OnyxChipsComponent,
    FleetModalHeaderDescriptionComponent,
    FleetModalDocumentsComponent,
    FleetModalWarningsComponent,
    FleetModalDetailsComponent,
    OnyxIconButtonComponent,
    OnyxDropdownDirective,
    ModalNoteComponent,
    OnyxTooltipDirective,
    FleetModalSummaryComponent,
  ],
  templateUrl: './fleet-modal.component.html',
  styleUrl: './fleet-modal.component.scss',
})
export class FleetModalComponent {
  protected readonly data = inject<FleetModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<void>);
  private readonly fleetService = inject(FleetService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly orderService = inject(OrdersService);
  private readonly fleetHelper = inject(FleetHelper);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly modalsStore = inject(ModalsStore);

  protected readonly I18N = 'fleet.fleetModal';

  protected readonly FleetModalSection = FleetModalSection;
  protected readonly DictionaryCode = DictionaryCode;
  protected readonly FleetRoute = FleetRoute;
  protected readonly FleetRouterStateKey = FleetRouterStateKey;

  private readonly documentsElement = viewChild(FleetModalDocumentsComponent, {
    read: ElementRef<HTMLElement>,
  });
  private readonly detailsElement = viewChild(FleetModalDetailsComponent, {
    read: ElementRef<HTMLElement>,
  });
  private readonly uuid: string;

  protected vehicle = signal<Fleet | null>(null);
  protected assignedVehicle = signal<Fleet | null>(null);
  protected order = signal<Order | null>(null);
  protected hasWarnings = signal<boolean | null>(null);
  protected section = this.modalsStore.getActiveSection(
    FleetModalComponent.name,
    () => this.sections(),
  );

  protected loading = signal(false);
  protected assignedVehicleLoading = signal(false);
  protected orderLoading = signal(false);

  protected error = signal(false);
  protected assignedVehicleError = signal(false);
  protected orderError = signal(false);

  protected vehicle$ = new Subject<void>();
  protected assignedVehicle$ = new Subject<void>();
  protected close$ = new Subject<void>();
  protected order$ = new Subject<void>();

  protected isArchived = computed(() =>
    this.vehicle() ? FleetHelper.isArchived(this.vehicle()!) : null,
  );
  protected isVehicle = computed(() =>
    this.vehicle() ? FleetTypeHelper.isVehicle(this.vehicle()!) : null,
  );
  protected options = computed(() =>
    this.vehicle()
      ? this.fleetHelper.getOptions(
          this.vehicle()!,
          { close$: this.close$ },
          FLEET_HEADER_ACTIONS,
        )
      : null,
  );
  protected sections = computed<OnyxChip<FleetModalSection>[]>(() => {
    const statuses = this.vehicle()
      ? FleetHelper.getVehicleExpiryStatuses(this.vehicle()!)
      : null;

    return [
      ...(!this.isArchived()
        ? [
            {
              name: 'labels.summary',
              value: FleetModalSection.SUMMARY,
            },
          ]
        : []),
      { name: 'labels.details', value: FleetModalSection.DETAILS },
      { name: 'labels.documents', value: FleetModalSection.DOCUMENTS },
      { name: 'labels.note', value: FleetModalSection.NOTE },
    ].map((section) => {
      if (!statuses) return section;

      const hasErrors = statuses.errors.some(
        (error) => error.section === section.value,
      );
      const hasWarnings = statuses.warnings.some(
        (warning) => warning.section === section.value,
      );
      const name = hasErrors
        ? 'error-filled'
        : hasWarnings
          ? 'alert-two-tone-filled'
          : null;

      return {
        ...section,
        ...(name && {
          leftIcon: { name, size: 20 },
          leftActiveIcon: { name: 'error-neutral-filled', size: 20 },
        }),
      };
    });
  });
  protected semiTrailerHeaderConfig = computed(() => {
    const semiTrailerSize =
      this.vehicle()?.additionalParameters?.semiTrailerSize;
    if (!semiTrailerSize) return null;

    return {
      icon: `semi-trailer-${semiTrailerSize}-outlined` as const,
      size: semiTrailerSize,
    };
  });

  constructor() {
    this.uuid = isString(this.data) ? this.data : this.data.uuid;

    this.vehicle$
      .pipe(
        tap(() => this.loading.set(true)),
        switchMap(() =>
          this.fleetService.getFleet(this.uuid).pipe(
            catchError(() => {
              this.vehicle.set(null);
              this.loading.set(false);
              this.error.set(true);

              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((vehicle) => {
        this.vehicle.set(vehicle);
        this.loading.set(false);
        this.error.set(false);

        this.assignedVehicle$.next();
      });

    this.assignedVehicle$
      .pipe(
        tap(() => this.assignedVehicleLoading.set(true)),
        switchMap(() => {
          const { trailer, vehicle } = this.vehicle() ?? {};
          const assignedVehicle = trailer?.uuid ?? vehicle?.uuid;
          if (!assignedVehicle) return of(null);

          return this.fleetService.getFleet(assignedVehicle).pipe(
            catchError(() => {
              this.assignedVehicle.set(null);
              this.assignedVehicleLoading.set(false);
              this.assignedVehicleError.set(true);

              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((vehicle) => {
        this.assignedVehicle.set(vehicle);
        this.assignedVehicleLoading.set(false);
        this.assignedVehicleError.set(false);
      });

    merge(this.order$, toObservable(this.vehicle, { injector: this.injector }))
      .pipe(
        tap(() => this.orderLoading.set(true)),
        switchMap(() => {
          const orderUuid = this.vehicle()?.orderUuid;
          if (!orderUuid) return of(null);

          return this.orderService.getOrder(orderUuid).pipe(
            catchError((response) => {
              this.order.set(null);
              this.orderLoading.set(false);
              this.orderError.set(true);

              throw response;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((order) => {
        this.order.set(order);
        this.orderLoading.set(false);
        this.orderError.set(false);
      });

    if (isString(this.data) || !FleetHelper.isFleetType(this.data)) {
      this.vehicle$.next();
    } else {
      this.vehicle.set(this.data);
      this.assignedVehicle$.next();
    }

    this.fleetService.reload$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.vehicle$.next());
  }

  protected edit(): void {
    this.fleetHelper.edit(this.uuid, { close$: this.close$ });
  }

  protected share(): void {
    const vehicle = this.vehicle();
    if (vehicle) this.fleetHelper.share(vehicle);
  }

  protected showOnMap(): void {
    const vehicle = this.vehicle();
    if (!vehicle) return;

    CommonHelper.showOnMap(this.router, vehicle.uuid, this.injector);
    this.close$.next();
  }

  protected showOnGantt(): void {
    const vehicle = this.vehicle();
    if (!vehicle) return;

    CommonHelper.showOnGantt(this.router, 'vehicle', vehicle.uuid);
    this.close$.next();
  }

  protected redirectToDocuments(): void {
    this.navigateToSection(FleetModalSection.DOCUMENTS, this.documentsElement);
  }

  protected redirectToDetails(): void {
    this.navigateToSection(FleetModalSection.DETAILS, this.detailsElement);
  }

  private navigateToSection(
    section: FleetModalSection,
    elementRef: Signal<ElementRef | undefined>,
  ): void {
    this.section.set([section]);

    toObservable(elementRef, { injector: this.injector })
      .pipe(
        filter((element) => !!element),
        first(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((element) => {
        const issueElement = element.nativeElement.querySelector(
          '#document-expiration-issue',
        );
        issueElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
  }
}
